/**
 * Jinda POS — Date Filter Utilities
 * 
 * Supports parsing multiple date formats (DD-MM-YYYY, YYYY-MM-DD, ISO, Date instances)
 * and calculating preset date ranges (all, today, last7days, this_week, this_month).
 */

export const formatDateYMD = (d: Date): string => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const formatDateTH = (dateInput: Date | string | null | undefined): string => {
    if (!dateInput) return '-';
    const ymd = parseDateToYMD(dateInput);
    if (!ymd) return String(dateInput);
    const [y, m, d] = ymd.split('-');
    return `${d}/${m}/${y}`;
};

export const parseDateToYMD = (raw: any): string | null => {
    if (!raw) return null;
    if (raw instanceof Date) {
        return isNaN(raw.getTime()) ? null : formatDateYMD(raw);
    }
    const str = String(raw).trim();
    if (!str) return null;

    // Matches DD-MM-YYYY or DD/MM/YYYY (with optional time)
    const dmyMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (dmyMatch) {
        const d = dmyMatch[1].padStart(2, '0');
        const m = dmyMatch[2].padStart(2, '0');
        const y = dmyMatch[3];
        return `${y}-${m}-${d}`;
    }

    // Matches YYYY-MM-DD or YYYY/MM/DD (with optional time / ISO)
    const ymdMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (ymdMatch) {
        const y = ymdMatch[1];
        const m = ymdMatch[2].padStart(2, '0');
        const d = ymdMatch[3].padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    const parsed = new Date(str);
    if (!isNaN(parsed.getTime())) {
        return formatDateYMD(parsed);
    }
    return null;
};

export type DatePresetType = 'all' | 'today' | 'yesterday' | 'last7days' | 'this_week' | 'this_month' | 'last_month' | 'custom';

export const getDateRangeForPreset = (preset: DatePresetType): { start: string; end: string } => {
    const today = new Date();

    if (preset === 'today') {
        const todayStr = formatDateYMD(today);
        return { start: todayStr, end: todayStr };
    }

    if (preset === 'yesterday') {
        const y = new Date(today);
        y.setDate(today.getDate() - 1);
        const yStr = formatDateYMD(y);
        return { start: yStr, end: yStr };
    }

    if (preset === 'last7days') {
        const d7 = new Date(today);
        d7.setDate(today.getDate() - 6);
        return { start: formatDateYMD(d7), end: formatDateYMD(today) };
    }

    if (preset === 'this_week') {
        const dayOfWeek = today.getDay(); // 0: Sunday, 1: Monday, ... 6: Saturday
        const diffToMonday = (dayOfWeek + 6) % 7; // Distance from Monday
        const monday = new Date(today);
        monday.setDate(today.getDate() - diffToMonday);
        return { start: formatDateYMD(monday), end: formatDateYMD(today) };
    }

    if (preset === 'this_month') {
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        return { start: formatDateYMD(monthStart), end: formatDateYMD(today) };
    }

    if (preset === 'last_month') {
        const lmStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lmEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        return { start: formatDateYMD(lmStart), end: formatDateYMD(lmEnd) };
    }

    return { start: '', end: '' };
};

export const isDateInRange = (dateVal: any, startDate: string, endDate: string): boolean => {
    if (!startDate && !endDate) return true;
    const ymd = parseDateToYMD(dateVal);
    if (!ymd) return true;

    if (startDate && endDate) {
        return ymd >= startDate && ymd <= endDate;
    }
    if (startDate) {
        return ymd >= startDate;
    }
    if (endDate) {
        return ymd <= endDate;
    }
    return true;
};
